<template>
  <button
    :disabled="loading"
    class="rounded-lg w-full h-11 font-medium text-sm transition-all duration-200 focus-visible:focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
    :class="providerStyles[props.provider].button"
    @click="handleLogin"
    :data-testid="`btn-${provider}-login`"
    type="button"
    :aria-busy="loading"
    :title="`Log in with ${capitalizedProvider}`"
  >
    <div class="flex items-center justify-center">
      <span :class="[providerStyles[props.provider].text, 'sr-only']">
        {{ $t(`auth.continueWith${capitalizedProvider}`) }}
      </span>
      <component v-if="!loading" aria-hidden="true" :is="iconComponent" :class="providerStyles[props.provider].icon" />
      <SpinnerIcon v-else class="size-4 animate-spin" />
    </div>

    <!-- Apple Sign-In Information Modal -->
    <AppleSignInModal :showing="showAppleModal" @close="showAppleModal = false" />
  </button>
</template>

<script setup>
import { ref, computed, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';
import GoogleIcon from '@/components/icons/GoogleIcon.vue';
import FacebookIcon from '@/components/icons/FacebookIcon.vue';
import AppleIcon from '@/components/icons/AppleIcon.vue';
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue';
import AppleSignInModal from '@/components/account/AppleSignInModal.vue';

const props = defineProps({
  provider: {
    type: String,
    required: true,
    validator: (value) => ['google', 'facebook', 'apple'].includes(value),
  },
});

const loading = ref(false);
const showAppleModal = ref(false);
const emailSubscriptionConsent = inject('emailSubscriptionConsent', ref(false));

const capitalizedProvider = computed(() => props.provider.charAt(0).toUpperCase() + props.provider.slice(1));

const iconComponent = computed(() => {
  const icons = {
    google: GoogleIcon,
    facebook: FacebookIcon,
    apple: AppleIcon,
  };
  return icons[props.provider];
});

const providerStyles = {
  google: {
    button: 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300',
    icon: 'size-6',
  },
  facebook: {
    button: 'bg-[#1877F2] hover:bg-[#166FE5] text-white border-0',
    icon: 'size-6 text-white',
  },
  apple: {
    button: 'bg-black hover:bg-gray-900 text-white border-0',
    icon: 'size-6 text-white',
  },
};

const { $firebaseAuth, $authProviders } = useNuxtApp();
const { $localeRootPath } = useNuxtApp();
const { locale } = useI18n();
const appStore = useAppStore();

const handleLogin = async () => {
  // Show modal first for Apple Sign-In
  if (props.provider === 'apple') {
    showAppleModal.value = true;
    // Wait for modal to be closed, then proceed with login
    await new Promise((resolve) => {
      const unwatch = watch(showAppleModal, (newValue) => {
        if (!newValue) {
          unwatch();
          resolve();
        }
      });
    });
  }

  if (!$firebaseAuth || !$authProviders) {
    console.error('Firebase Auth not initialized');
    return;
  }

  loading.value = true;

  try {
    const { signInWithPopup } = await import('firebase/auth');
    const provider = $authProviders[props.provider];

    if (!provider) {
      throw new Error(`Provider ${props.provider} not configured`);
    }

    const result = await signInWithPopup($firebaseAuth, provider);
    const idToken = await result.user.getIdToken();
    const isNewUser = result._tokenResponse?.isNewUser || false;

    const newsletterSignup = appStore.footerData?.newsletterSignup;
    const mailchimpListId = newsletterSignup?.mailchimpListId;

    const response = await $fetch('/api/auth/sociallogin', {
      method: 'POST',
      body: {
        idToken,
        provider: props.provider,
        isNewUser,
        emailSubscriptionConsent: emailSubscriptionConsent.value,
        locale: locale.value,
        mailchimpListId,
      },
    });

    if (response.success) {
      const { fetch } = useUserSession();
      await fetch();

      const redirectUrl =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('fromUri') || $localeRootPath('/account')
          : $localeRootPath('/account');

      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('fromUri');
      }

      await navigateTo(redirectUrl);
    }
  } catch (error) {
    console.error('Social login error:', error);

    if (error.code === 'auth/popup-closed-by-user') {
      return;
    } else if (error.code === 'auth/popup-blocked') {
      console.error('Popup blocked. Please allow popups for this site.');
    } else {
      console.error('Login failed. Please try again.');
    }
  } finally {
    loading.value = false;
  }
};
</script>
